/**
 * MainPanelHeader — controls bar above results.
 *
 * Row 1: [X results for "query"]  [Save Search]     [Sort: Best Match ▼]  [grid|list]
 * Row 2: [All] [New] [Used] [active-filters...]                            [Clear all]
 */

import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { SlidersHorizontal, Heart } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { NamedLink } from '../../../components';

import css from './MainPanelHeader.module.css';

// ── Icons ────────────────────────────────────────────────────────────

const GridIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const ListIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1" y="2" width="14" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="1" y="6.75" width="14" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="1" y="11.5" width="14" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const DismissIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ── Save Search ──────────────────────────────────────────────────────

const SAVED_SEARCHES_KEY = 'pcific_saved_searches';

function getSavedSearches() {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem(SAVED_SEARCHES_KEY) : null;
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function toggleSavedSearch(url) {
  try {
    const saved = getSavedSearches();
    const exists = saved.findIndex(s => s.url === url);
    if (exists >= 0) {
      saved.splice(exists, 1);
    } else {
      saved.unshift({ url, timestamp: Date.now() });
    }
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(saved.slice(0, 20)));
    return exists < 0; // returns true if added
  } catch {
    return false;
  }
}

function isSearchSaved(url) {
  return getSavedSearches().some(s => s.url === url);
}

// ── Component ────────────────────────────────────────────────────────

const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
    viewMode = 'grid',
    onSetViewMode,
    keywords,
    activeFilters = [],
    onConditionChange,
    currentCondition = null,
    aiIntentCard = null,
    onToggleFilters = null,
    filtersOpen = false,
    categoryBreadcrumbs = [],
  } = props;

  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  // Save search state
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [isSaved, setIsSaved] = useState(() => isSearchSaved(currentUrl));

  const handleSaveSearch = useCallback(() => {
    const added = toggleSavedSearch(currentUrl);
    setIsSaved(added);
  }, [currentUrl]);

  // Results message
  const resultsMessage = searchInProgress ? (
    <FormattedMessage id="MainPanelHeader.loadingResults" />
  ) : keywords ? (
    <FormattedMessage
      id="MainPanelHeader.foundResultsForKeyword"
      values={{
        count: resultsCount,
        keyword: <strong className={css.keywordHighlight}>{keywords}</strong>,
      }}
    />
  ) : (
    <FormattedMessage id="MainPanelHeader.foundResults" values={{ count: resultsCount }} />
  );

  const conditionPills = [
    { value: null, labelId: 'MainPanelHeader.allListings' },
    { value: 'new', labelId: 'MainPanelHeader.conditionNew' },
    { value: 'used', labelId: 'MainPanelHeader.conditionUsed' },
  ];

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div className={classes}>
      {/* ── Row 1: Info bar ── */}
      <div className={css.infoRow}>
        <h2 className={css.searchResultSummary}>
          <span className={css.resultsFound}>{resultsMessage}</span>
        </h2>

        <div className={css.infoRight}>
          {/* Save search */}
          {listingsAreLoaded && keywords && (
            <button
              type="button"
              className={classNames(css.saveBtn, { [css.saveBtnActive]: isSaved })}
              onClick={handleSaveSearch}
              aria-label={isSaved ? 'Unsave search' : 'Save search'}
            >
              <Heart size={14} fill={isSaved ? 'currentColor' : 'none'} />
              <span>{isSaved ? 'Saved' : 'Save'}</span>
            </button>
          )}

          {/* Sort */}
          {isSortByActive ? (
            <div className={css.sortyByWrapper}>
              <span className={css.sortyBy}>
                <FormattedMessage id="MainPanelHeader.sortBy" />
              </span>
              {sortByComponent}
            </div>
          ) : null}

          {/* View toggle */}
          {onSetViewMode ? (
            <div className={css.viewToggle}>
              <button
                className={classNames(css.viewToggleBtn, {
                  [css.viewToggleBtnActive]: viewMode === 'grid',
                })}
                onClick={() => onSetViewMode('grid')}
                aria-label={intl.formatMessage({ id: 'MainPanelHeader.viewGrid' })}
                aria-pressed={viewMode === 'grid'}
                type="button"
              >
                <GridIcon />
              </button>
              <button
                className={classNames(css.viewToggleBtn, {
                  [css.viewToggleBtnActive]: viewMode === 'list',
                })}
                onClick={() => onSetViewMode('list')}
                aria-label={intl.formatMessage({ id: 'MainPanelHeader.viewList' })}
                aria-pressed={viewMode === 'list'}
                type="button"
              >
                <ListIcon />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* AI intent card slot */}
      {aiIntentCard}

      {/* ── Row 2: Promoted filters ── */}
      <div className={css.controlsRow}>
        <div className={css.pillsLeft}>
          {/* Filter drawer toggle (desktop < 1024px, or manual toggle) */}
          {onToggleFilters && (
            <button
              type="button"
              className={classNames(css.filterToggleBtn, {
                [css.filterToggleBtnActive]: filtersOpen,
              })}
              onClick={onToggleFilters}
              aria-label="Toggle filters"
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal size={16} strokeWidth={1.8} />
              <span>Filters</span>
            </button>
          )}

          {/* Category breadcrumb pills (desktop only, hidden on mobile where CategoryNavStrip shows) */}
          {categoryBreadcrumbs.map(crumb => (
            <NamedLink
              key={crumb.label}
              name={crumb.clearRouteName}
              params={crumb.clearRouteParams}
              className={css.categoryPill}
            >
              <span className={css.activePillLabel}>{crumb.label}</span>
              <DismissIcon />
            </NamedLink>
          ))}

          {/* Condition tabs */}
          {listingsAreLoaded && onConditionChange
            ? conditionPills.map(pill => (
                <button
                  key={pill.value || 'all'}
                  type="button"
                  className={classNames(css.filterPill, {
                    [css.filterPillActive]: currentCondition === pill.value,
                  })}
                  onClick={() => onConditionChange(pill.value)}
                >
                  {intl.formatMessage({ id: pill.labelId })}
                </button>
              ))
            : null}

          {/* Active filter dismiss pills */}
          {activeFilters.map(filter => (
            <button
              key={filter.key}
              type="button"
              className={css.activePill}
              onClick={filter.onRemove}
              aria-label={intl.formatMessage(
                { id: 'MainPanelHeader.removeFilter' },
                { label: filter.label }
              )}
            >
              <span className={css.activePillLabel}>{filter.label}</span>
              <DismissIcon />
            </button>
          ))}

          {/* Clear all */}
          {hasActiveFilters && (
            <button
              type="button"
              className={css.clearAllBtn}
              onClick={() => activeFilters.forEach(f => f.onRemove())}
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};

export default MainPanelHeader;
